/**
 * Experience page (Pages/Experience.vue) without Vue: PageLoader.vue over the home animation
 * (Components/Home/Animation/*), ported line for line in page-loader.ts, animation.ts and
 * steps.ts. Mount order of the Vue page: PageLoader subscribes to « loading:progress », then the
 * page adds html.homepage, emits "start" and, outside an iframe, falls back to /about-us after
 * 5 s unless the first video is ready (animation:ready) before.
 *
 * Experience.vue loads HomeAnimation with defineAsyncComponent: the chunk is requested while the
 * page renders (before any onMounted), and the animation (with its first video, whose metadata
 * cancels the 5 s fallback) only mounts once the chunk has arrived, after the timer has started.
 * animation.ts is therefore a dynamic import, requested first and mounted when it resolves: the
 * video gets 5 s minus the chunk load time on both pages, not the full 5 s on the Blade one.
 *
 * Like app.ts: storeUtm (bootstrap.ts) and Sentry (sentry-blade.ts, same config); no RudderStack
 * (the Inertia app does not load it).
 */
import "../storeUtm";
import "../sentry-blade";
import mitt from "mitt";
import type { LoadingEvents } from "./animation";
import { mountPageLoader } from "./page-loader";
import { useTimeout } from "./timeout";

const root = document.getElementById("experience-root");

if (root) {
    // defineAsyncComponent(() => import("Animation.vue")): load() runs at render time.
    const animationChunk = import("./animation");

    const emitter = mitt<LoadingEvents>();
    const { launchTimeout, cancelTimeout } = useTimeout();

    // <PageLoader v-if="showVideo" />: its onMounted (subscription) runs before the page's.
    const loader = root.querySelector<HTMLElement>(".loader");
    if (loader) mountPageLoader(loader, emitter);

    // Experience.vue onMounted
    document.documentElement.classList.add("homepage");
    emitter.emit("loading:progress", "start");

    if (window.self === window.top) {
        /*
            Start timeout of 5s to stop loading animation.
            It will be cancel when animation is ready:
            - If it's canceled before the timeout, it would do nothing
            - Else it will disable animation
        */
        launchTimeout(5000, () => {
            window.location.href = "/about-us";
        });
    }

    // <HomeAnimation @animation:ready="cancelTimeout" />, mounted once the async chunk resolves.
    void animationChunk.then(({ mountAnimation }) => {
        mountAnimation({
            root,
            emitter,
            onReady: cancelTimeout,
            marketplaceUrl: root.dataset.marketplaceUrl ?? "/marketplace?fromVideo=1",
        });
    });
}
